/**
 * Shared code between client and server
 * Useful to share types between client and server
 * and/or small pure JS functions that can be used on both client and server
 */

/**
 * Example response type for /api/demo
 */
export interface DemoResponse {
  message: string;
}

/** GET /api/crm/calling/call-logs — single row */
export interface CallingCallLogEntry {
  id: string;
  createdAt: string;
  initiatorEmail: string;
  contactId: string | null;
  contactName: string | null;
  dealId: string | null;
  dealTitle: string | null;
  surface: "mobile" | "table" | null;
}

/** GET /api/crm/calling/call-logs — paginated list */
export interface CallingCallLogsListResponse {
  entries: CallingCallLogEntry[];
  hasMore: boolean;
}

export type CallingCallLogSeriesScope = "me" | "user" | "team";

export type CallingCallLogSeriesPoint = {
  /** Bratislava-local day key: YYYY-MM-DD */
  day: string;
  /** Number of call events for the given day */
  count: number;
};

export type CallingCallLogSeriesResponse = {
  tz: "Europe/Bratislava";
  scope: CallingCallLogSeriesScope;
  /** Present when scope=user */
  userEmail?: string;
  /** Inclusive date range in Bratislava day keys */
  dateFrom: string;
  dateTo: string;
  series: CallingCallLogSeriesPoint[];
};

export type CallingCallerOption = {
  email: string;
  label: string;
};

export type CallingCallersResponse = {
  users: CallingCallerOption[];
};

/**
 * Generated Route Leg interface (shared between client and server)
 */
export interface GeneratedRouteLeg {
  id?: string;
  month: string; // YYYY-MM
  date: string; // YYYY-MM-DD
  vehicle_id: string;
  from_location: string;
  to_location: string;
  from_address?: string;
  to_address?: string;
  distance_km: number;
  duration_minutes?: number;
  route_type?: string;
  notes?: string;
  created_at?: string;
  created_by?: string;
  deal_id?: string;
  departure_time?: string; // TIME format (HH:MM:SS)
  arrival_time?: string; // TIME format (HH:MM:SS)
  client_duration_minutes?: number | null; // Duration at client location in minutes
  sequence_number?: number | null; // Kniha jázd: per-vehicle per-year record number
}

/**
 * Image Folder interfaces
 */
export type SubfolderType = 'bemeres' | 'munkaink' | 'marketing';
export type PrelisType = string; // Prelis types are now dynamic, managed through lookup table

export type ImageFolderCategory = 'bemeres' | 'realizacio' | 'egyeb';

export interface ImageFolder {
  id: string;
  name: string;
  deal_id?: string | null;
  panel_structure?: string | null;
  color?: string | null;
  prelis_type?: PrelisType | null;
  brand?: string | null;
  folder_type?: string | null;
  model?: string | null;
  install_date?: string | null;
  category?: ImageFolderCategory | null;
  /** Set when project.photos_uploaded marketing webhook succeeded (idempotency). */
  marketing_webhook_sent_at?: string | null;
  /** Last marketing webhook failure (no secrets). */
  marketing_webhook_last_error?: string | null;
  /** Facebook upload status from marketing bot callback. */
  fb_upload_status?: "posted" | "partial" | "failed" | "pending" | "skipped" | null;
  fb_uploaded_at?: string | null;
  fb_post_urls?: Record<string, string | null> | null;
  fb_upload_error?: string | null;
  /** Instagram upload status from marketing bot callback. */
  ig_upload_status?: "posted" | "failed" | "pending" | "skipped" | null;
  ig_uploaded_at?: string | null;
  ig_permalink?: string | null;
  ig_upload_error?: string | null;
  social_callback_sent_at?: string | null;
  /**
   * Marketing reference class (API field `category` on /api/social/references).
   * Separate from image_folders.category (bemeres|realizacio|egyeb).
   */
  social_category?: "residential" | "industrial" | null;
  social_allowed?: boolean;
  title_public?: string | null;
  handover_at?: string | null;
  quantity?: number | null;
  location_town?: string | null;
  /** Last successful Facebook social post (UTC); null = never. Fair rotation for references API. */
  last_social_posted_at?: string | null;
  /** Who last set social upload status: CRM bulk mark vs marketing bot webhook. */
  social_status_source?: "manual" | "bot" | null;
  /** Photo counts by subfolder (for row color: Munkáink→green, Bemérésre only→red, other→grey) */
  photo_counts?: { bemeres: number; munkaink: number; marketing: number };
  created_by: string;
  created_at: string;
  updated_at: string;
  deals?: {
    id: string;
    title: string;
    contact_id: string;
  } | null;
}

/** GET /api/images/photos/facets — counts per dimension for all bemeres+munkaink photos */
export type GalleryPhotoFilterFacets = {
  panelStructure: Record<string, number>;
  brand: Record<string, number>;
  folderType: Record<string, number>;
  model: Record<string, number>;
  color: Record<string, number>;
  prelisType: Record<string, number>;
};

export interface ImageFolderPhoto {
  id: string;
  folder_id: string;
  subfolder_type: SubfolderType;
  /** WGS84 from EXIF at upload; null if missing or not a geotagged image */
  exif_latitude?: number | null;
  exif_longitude?: number | null;
  /** Bemérés: termék címke (pl. "Garázskapu 1") – termékenként külön listázás */
  product_slug?: string | null;
  r2_key: string;
  file_url: string;
  file_name: string;
  file_size?: number | null;
  mime_type?: string | null;
  thumbnail_url?: string | null;
  order_index: number;
  created_at: string;
  updated_at: string;
  /** EXIF / client hint capture time (UTC ISO); null if unset (e.g. in-app camera) */
  captured_at?: string | null;
  /** Gallery ordering: max(captured_at, created_at) when captured_at set; else created_at */
  gallery_sort_at?: string | null;
}

export interface ImageFolderWithPhotos extends ImageFolder {
  photos: {
    bemeres: ImageFolderPhoto[];
    munkaink: ImageFolderPhoto[];
    marketing: ImageFolderPhoto[];
  };
  photoCount: number;
}

export interface ImageFolderFilters {
  panelStructure?: string | string[];
  brand?: string | string[];
  folderType?: string | string[];
  model?: string | string[];
  color?: string | string[];
  prelisType?: PrelisType | PrelisType[];
  dateFrom?: string;
  dateTo?: string;
  name?: string;
  category?: ImageFolderCategory | ImageFolderCategory[];
  fbUploadStatus?: string | string[];
  igUploadStatus?: string | string[];
  /** Combined social filter: all | posted | unposted */
  socialPosted?: "all" | "posted" | "unposted";
}

export interface ImageFoldersListResponse {
  folders: ImageFolder[];
}

export interface ImageFolderResponse {
  folder: ImageFolderWithPhotos;
}

export interface ImageFolderPhotosResponse {
  photos: ImageFolderPhoto[];
  total: number;
}

/**
 * Social media creative assets (generated from gallery photos)
 */
export type SocialAssetVariant = "ig_feed_square" | "ig_feed_portrait" | "ig_story" | "fb_feed";
export type SocialAssetStatus = "draft" | "generated" | "approved" | "publishing" | "published" | "failed";

export interface SocialAsset {
  id: string;
  source_photo_id: string;
  variant: SocialAssetVariant;
  status: SocialAssetStatus;
  r2_key: string;
  file_url: string;
  mime_type?: string | null;
  width?: number | null;
  height?: number | null;
  created_by?: string | null;
  approved_by?: string | null;
  published_at?: string | null;
  fb_post_id?: string | null;
  fb_photo_id?: string | null;
  ig_container_id?: string | null;
  ig_media_id?: string | null;
  meta?: Record<string, unknown> | null;
  error?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface SocialAssetsGenerateRequest {
  photoIds: string[];
  variants: SocialAssetVariant[];
  preset: string;
  logo?: { enabled: boolean; position: "top_left" | "top_right" | "bottom_left" | "bottom_right"; scale: number } | null;
  overlayText?: { headline?: string; cta?: string } | null;
  caption?: string | null;
}

export interface SocialAssetsGenerateResponse {
  assets: SocialAsset[];
}

/**
 * Product type config (bemérési mezők típusonként).
 * Label can be localized (label_hu, label_sk, label_en); label is fallback for legacy data.
 */
export interface MeasurementFieldConfig {
  key: string;
  /** @deprecated use label_hu / label_sk / label_en */
  label?: string;
  label_hu?: string | null;
  label_sk?: string | null;
  label_en?: string | null;
  required: boolean;
  type: 'text' | 'textarea' | 'dimensions' | 'number' | 'boolean';
}

/** Returns the localized label for a measurement field (hu/sk/en); falls back to legacy label. */
export function getMeasurementFieldLabel(field: MeasurementFieldConfig, lang: 'hu' | 'sk' | 'en'): string {
  const v = lang === 'hu' ? field.label_hu : lang === 'sk' ? field.label_sk : field.label_en;
  if (v != null && String(v).trim() !== '') return String(v).trim();
  return field.label_hu ?? field.label_sk ?? field.label_en ?? field.label ?? '';
}

export interface ProductType {
  id: string;
  name: string;
  name_hu?: string | null;
  name_sk?: string | null;
  name_en?: string | null;
  pipelineCategory: string; // 'garazskapuk' | 'klimak'
  pipeline_category?: string;
  orderIndex?: number;
  order_index?: number;
  measurementFields: MeasurementFieldConfig[];
  measurement_fields?: MeasurementFieldConfig[];
}

/**
 * CRM Deal interfaces
 */
export interface GateMeasurement {
  id: string; // UUID minden kapunál
  widths: string[]; // Minimum 2 szélesség mérés
  heights: string[]; // Minimum 2 magasság mérés
  notes: string; // Megjegyzések
  surface: string; // Povrch (panel_structure)
  color?: string; // Farba
  prelis?: string; // Prelis
  surfaceType?: 'preset' | 'custom'; // Legacy: korábbi felület típus
}

/** One product item in a deal (e.g. Garázskapu 1, Klíma 1). Fields depend on productType. */
export interface DealProductItem {
  id: string;
  productType: string; // ProductType name e.g. "Garázskapu"
  productTypeId?: string; // UUID from product_types
  index: number; // 1-based within same productType
  widths?: string[];
  heights?: string[];
  surface?: string;
  color?: string;
  prelis?: string;
  notes?: string;
  depth?: string; // Pergola mélység (mm)
  type?: string; // Klíma/hőszivattyú típus
  power?: string; // Teljesítmény
}

export interface MeasurementData {
  gates: GateMeasurement[]; // Legacy: olvasáskor mapeljük products-re ha nincs products
  products?: DealProductItem[];
  completedAt?: string; // Befejezés időpontja
}

export interface Deal {
  id: string;
  title: string;
  stageId: string;
  pipelineId: string;
  contactId: string;
  value?: number | null;
  currency?: string | null;
  /** Planned close date (YYYY-MM-DD). API/JSON name; persisted canonically as `closing_date` (legacy column kept identical in DB by trigger). */
  closingDate?: string | null;
  createdAt: string;
  ownerEmail?: string | null;
  width?: number | null;
  height?: number | null;
  color?: string | null;
  surface?: string | null;
  emboss?: string | null;
  city?: string | null;
  address?: string | null;
  company?: string | null;
  /** FinStat snapshot from configurator (ico, dic, ic_dph, meno_firmy, sidlo_firmy). */
  company_finstat?: Record<string, unknown> | null;
  measurementData?: MeasurementData | null;
  /** Ketté bontott deal: a másik pipeline-beli testvér deal id-ja */
  siblingDealId?: string | null;
  sibling_deal_id?: string | null;
  contacts?: Contact | null;
  contact?: Contact | null;
  stage_id?: string;
  pipeline_id?: string;
  created_at?: string;
  /** Canonical DB column for planned close (DATE). Always in sync with `closingDate` on read/write. */
  closing_date?: string;
  /** Rövid összefoglaló a kártyához (pl. "2× Garázskapu, 1× Klíma") */
  productSummary?: string | null;
  /** Facebook Lead Ads automatikus deal */
  facebookLeadgenId?: string | null;
  facebookDataIncomplete?: boolean | null;
  facebookQuoteQuality?: "priced" | "placeholder" | "failed" | string | null;
  /** ID of the superfaktura_documents row (type='quote') chosen as the root of this deal's document chain. */
  anchorDocumentId?: string | null;
}

export interface Contact {
  id: string;
  name: string;
  /** Obchodné meno / cégnév (kapcsolattartó a `name` mezőben). */
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  street?: string | null;
  isCompany?: boolean;
  ico?: string | null;
}

export interface Stage {
  id: string;
  name: string;
  pipelineId: string;
  order?: number;
  orderIndex?: number;
  order_index?: number;
  pipeline_id?: string;
  wipLimit?: number | null;
  wip_limit?: number | null;
  measure?: boolean | null;
}

export interface Pipeline {
  id: string;
  name: string;
  parentPipelineId?: string | null;
  parent_pipeline_id?: string | null;
  order?: number;
  orderIndex?: number;
  order_index?: number;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

export interface Board {
  pipelines: Pipeline[];
  stages: Stage[];
  deals: Deal[];
  contacts: Contact[];
}

/**
 * CRM API Response types (shared between client and server)
 */

export interface BoardResponse {
  pipelines: Pipeline[];
  stages: Stage[];
  deals: Deal[];
  contacts: Contact[];
}

export interface ContactsListResponse {
  contacts: Contact[];
}

export interface ContactsListPaginatedResponse {
  contacts: Contact[];
  total: number;
}

/** GET /api/crm/contacts/by-ids */
export interface ContactsByIdsResponse {
  contacts: Contact[];
}

export interface StagesListResponse {
  stages: Stage[];
}

export interface PipelinesListResponse {
  pipelines: Pipeline[];
}

export interface DealsListResponse {
  deals: Deal[];
  total: number;
  hasMore?: boolean;
}

/** GET /api/crm/deals/search — all stages including Deal Lost; slim deals + optional embedded contact */
export interface CrmDealsSearchResponse {
  deals: Array<Deal & { contact?: Contact | null }>;
}

/** GET /api/crm/notes/search — all deal notes (incl. lost); enriched with dealTitle + contact */
export interface CrmNotesSearchResponse {
  notes: Array<
    Record<string, unknown> & {
      id?: string;
      body?: string;
      dealId?: string;
      dealTitle?: string;
      contactId?: string | null;
      createdAt?: string;
      contact?: Contact | null;
    }
  >;
}

// Note: The actual response has both success (boolean) and results object
// The spread operator causes the results.success (number) to override the boolean
// This interface reflects the actual runtime structure where success is a number
export interface BatchOperationResponse {
  success: number; // Count of successful operations (overrides the boolean in the code)
  failed: number; // Count of failed operations
  errors: string[]; // Array of error messages
}

/**
 * Route Comparison interfaces
 */
export interface VehicleStats {
  vehicleId: string;
  vehicleName: string;
  totalKm: number;
  targetKm: number;
  percentage: number;
}

export interface RouteStats {
  totalKm: number;
  measurementRoundsKm: number;
  fixedPointsKm: number;
  clientVisitsKm: number;
  returnToBaseKm: number;
  vehicleStats: VehicleStats[];
  daysCount: number;
  visitsCount: number;
  measurementRoundsCount: number;
  fixedPointsCount: number;
  clientVisitsCount: number;
}

export interface ComparisonResult {
  success: boolean;
  v1: {
    routes: GeneratedRouteLeg[];
    stats: RouteStats;
    error?: string | null;
  };
  v2: {
    routes: GeneratedRouteLeg[];
    stats: RouteStats;
    error?: string | null;
  };
  month: string;
}

/** Bevásárló lista szekció (Kocsiba, Rendelés, vagy egyéni pl. OBI, IKEA) */
export interface ShoppingListSection {
  id: string;
  name: string;
  sort_order: number;
  /** Lucide icon name, e.g. car, shopping-cart, list */
  icon?: string | null;
}

/** Bevásárló lista tétel; section_id alapján tartozik egy szekcióhoz */
export interface ShoppingListItem {
  id: string;
  section_id: string;
  title: string;
  note: string | null;
  checked: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface ShoppingListResponse {
  sections: ShoppingListSection[];
  items: ShoppingListItem[];
  pending_total: number;
}

/** Subpipeline row (dashboard filter + heatmap) */
export interface CrmDashboardHeatmapSubpipelineMeta {
  pipelineId: string;
  label: string;
  labelHu: string;
  labelSk: string;
}

/** GET /api/crm/dashboard — CRM metrics snapshot */
export type DashboardConversionPeriod = "week" | "month" | "quarter";

export interface CrmDashboardConversionRow {
  label: string;
  leadsIn: number;
  won: number;
  lost: number;
  conversionRate: number | null;
}

export interface CrmDashboardDealStageMovementDailyRow {
  dateKey: string;
  dateLabel: string;
  positive: number;
  negative: number;
  callsTeam: number;
  highCallVolume: boolean;
}

/** Per-owner deal stage moves split by whether that user had a Volanie call that day. */
export interface CrmDashboardStageMovementCallerSplit {
  key: string;
  label: string;
  positiveMovesOnCallDays: number;
  negativeMovesOnCallDays: number;
  positiveMovesOnOtherDays: number;
  negativeMovesOnOtherDays: number;
}

/** Side-panel stats for the stage movement chart (same definitions as daily rows). */
export interface CrmDashboardStageMovementSummary {
  totalPositiveMoves: number;
  totalNegativeMoves: number;
  avgPositiveMovesPerDay: number;
  /** Sum of `positive` on days where `callsTeam > 0` (team / „Össz”). */
  positiveMovesOnCallDays: number;
  /** Sum of `negative` on days where `callsTeam > 0`. */
  negativeMovesOnCallDays: number;
  daysInRange: number;
  /** Days with at least one team Volanie tel_click. */
  daysWithTeamCalls: number;
  /** Share of positive moves that fell on a call day; null when `totalPositiveMoves === 0`. */
  positiveOnCallDaysSharePct: number | null;
  /** Deals matching dashboard filters (pipeline + created_at range). */
  newDealsInFilterPeriod: number;
  /** Petrás / Takács — moves on deals they own (`ownerEmail`), split by their call days. */
  callerSplits?: CrmDashboardStageMovementCallerSplit[];
}

export interface CrmDashboardSnapshot {
  activeDeals: { activeCount: number; pipelineValue: number };
  /** Subpipelines for dashboard filter dropdown (same shape as heatmap) */
  subpipelines?: CrmDashboardHeatmapSubpipelineMeta[];
  pipelineStages: Array<{
    stageId: string;
    label: string;
    labelHu: string;
    count: number;
    totalValue: number;
    sortOrder: number;
  }>;
  monthlyTrend: Array<{
    month: string;
    monthKey: string;
    offerValue: number;
    wonValue: number;
  }>;
  leadSources: Array<{ source: string; count: number; percentage: number }>;
  conversionWeek: CrmDashboardConversionRow[];
  conversionMonth: CrmDashboardConversionRow[];
  conversionQuarter: CrmDashboardConversionRow[];
  kpis90d: {
    conversionPct: number | null;
    avgCycleDays: number | null;
    won90: number;
    lost90: number;
  };
  dealsCreatedDaily: Array<{
    dateKey: string;
    dateLabel: string;
    facebook: number;
    configurator: number;
    manual: number;
  }>;
  /** Bratislava calendar days: last stage_changed per deal per day → positive/lost counts; team Volanie tel_click / day */
  dealStageMovementDaily: CrmDashboardDealStageMovementDailyRow[];
  /** Aggregated counters for the stage movement card side panel */
  stageMovementSummary: CrmDashboardStageMovementSummary;
}

/** GET /api/crm/dashboard/heatmap */
/** All pipelines (labels for stage list); same shape as subpipeline meta. */
export type CrmDashboardHeatmapPipelineMeta = CrmDashboardHeatmapSubpipelineMeta;

export interface CrmDashboardHeatmapStageMeta {
  stageId: string;
  pipelineId: string;
  label: string;
  labelHu: string;
  labelSk: string;
  /** Hex fill for legend + heat blobs, e.g. #2563eb */
  legendColor?: string;
}

export interface CrmDashboardHeatmapPoint {
  pipelineId: string;
  stageId: string;
  lat: number;
  lng: number;
  count: number;
  label: string;
}

export interface CrmDashboardHeatmapResponse {
  subpipelines: CrmDashboardHeatmapSubpipelineMeta[];
  /** Full pipeline list for localized sublabels on stages */
  pipelines?: CrmDashboardHeatmapPipelineMeta[];
  stages: CrmDashboardHeatmapStageMeta[];
  points: CrmDashboardHeatmapPoint[];
  /** Deals skipped: no related contact location and no usable deal address/city */
  dealsWithoutLocation: number;
  dealsGeocodedFailed: number;
}
